// src/contexts/GameContext.js
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { api } from "../services/api";
import socketService from "../services/socket";
import { useAuth } from "./AuthContext";

const GameContext = createContext(null);
const STAKE_AMOUNTS = { starter: 10, pro: 20, highroller: 50 };

export function GameProvider({ children }) {
  const { refreshBalance } = useAuth();

  const [showStakeModal, setShowStakeModal] = useState(false);
  const [showCartelaModal, setShowCartelaModal] = useState(false);
  const [inGame, setInGame] = useState(false);
  const [lobbyPlayerCount, setLobbyPlayerCount] = useState(0);
  const [isSpectator, setIsSpectator] = useState(false);
  const [myCartelaDetails, setMyCartelaDetails] = useState([]); // [{id, card_number, grid}]
  const [selectedStakeId, setSelectedStakeId] = useState(null);
  const [activeRoom, setActiveRoom] = useState(null);
  const [availableCartelas, setAvailableCartelas] = useState([]); // static catalog
  const [requiredLines, setRequiredLines] = useState(1);

  // Lobby state — pushed by the server, identical for every browser watching
  // the same room.
  const [lobbyEndsAt, setLobbyEndsAt] = useState(null);
  const [takenCartelaIds, setTakenCartelaIds] = useState([]);
  const [myCartelaIds, setMyCartelaIds] = useState([]);
  const [initialLiveState, setInitialLiveState] = useState(null); // for mid-game joiners
  const [estimatedPot, setEstimatedPot] = useState(0);

  const [joinError, setJoinError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleLobbyState = ({
      endsAt,
      takenCartelaIds,
      myCartelaIds,
      playerCount,
      requiredLines,
    }) => {
      setLobbyEndsAt(endsAt);
      setLobbyPlayerCount(playerCount);
      setTakenCartelaIds(takenCartelaIds);
      setMyCartelaIds(myCartelaIds);
      setIsSpectator(false);
      setMyCartelaDetails([]);
      setShowCartelaModal(true);
      setRequiredLines(requiredLines ?? 1); // NEW
    };
    const handleYourCartelas = ({ cartelas }) => setMyCartelaDetails(cartelas);
    const handleGameState = ({
      room,
      liveState,
      isSpectator: spectator,
      cartelas,
      requiredLines,
    }) => {
      setActiveRoom(room);
      setIsSpectator(spectator);
      setInitialLiveState(liveState);
      setMyCartelaDetails(cartelas ?? []);
      setShowCartelaModal(false);
      setRequiredLines(requiredLines ?? 1); // NEW
      setInGame(true);
    };
    const handleLobbyUpdate = ({
      takenCartelaIds,
      playerCount,
      estimatedPot,
    }) => {
      setTakenCartelaIds(takenCartelaIds);
      setLobbyPlayerCount(playerCount);
      setEstimatedPot(estimatedPot ?? 0); // ADD THIS LINE
    };
    const handleLobbyMine = ({ myCartelaIds }) => setMyCartelaIds(myCartelaIds);
    socketService.on("lobby_mine", handleLobbyMine);
    // ...and in the cleanup: socketService.off("lobby_mine", handleLobbyMine);

    const handleCartelaRejected = ({ reason }) => setJoinError(reason);

    const handleGameStarted = () => {
      setShowCartelaModal(false);
      setInGame(true);
      refreshBalance();
    };

    const handleLobbyCancelled = ({ reason }) => {
      setShowCartelaModal(false);
      setActiveRoom(null);
      setLobbyEndsAt(null);
      setTakenCartelaIds([]);
      setMyCartelaIds([]);
      setJoinError(
        reason === "no_players"
          ? "Nobody joined in time. Pick a stake to start a new round."
          : "Could not start the round. Please try again.",
      );
    };

    socketService.on("lobby_state", handleLobbyState);
    socketService.on("lobby_update", handleLobbyUpdate);
    socketService.on("cartela_rejected", handleCartelaRejected);
    socketService.on("game_started", handleGameStarted);
    socketService.on("lobby_cancelled", handleLobbyCancelled);
    socketService.on("game_state", handleGameState);
    socketService.on("your_cartelas", handleYourCartelas);

    return () => {
      socketService.off("lobby_mine", handleLobbyMine);
      socketService.off("lobby_state", handleLobbyState);
      socketService.off("lobby_update", handleLobbyUpdate);
      socketService.off("cartela_rejected", handleCartelaRejected);
      socketService.off("game_started", handleGameStarted);
      socketService.off("lobby_cancelled", handleLobbyCancelled);
      socketService.off("game_state", handleGameState);
      socketService.off("your_cartelas", handleYourCartelas);
    };
  }, [refreshBalance]);

  const openStakeModal = useCallback(() => {
    setJoinError(null);
    setShowStakeModal(true);
  }, []);

  const returnToSelection = useCallback(() => {
    const roomId = activeRoom?.id;

    setInGame(false);
    setIsSpectator(false);
    setMyCartelaDetails([]);
    setLobbyEndsAt(null);
    setTakenCartelaIds([]);
    setMyCartelaIds([]);
    setInitialLiveState(null);
    refreshBalance();

    if (roomId) {
      // The server already reset this room to "waiting" and bumped
      // round_number by the time bingo_confirmed/game_over fired, so this
      // just opens a brand-new lobby for the next round.
      socketService.selectStake(roomId);
    } else {
      setActiveRoom(null);
      setSelectedStakeId(null);
    }
  }, [activeRoom, refreshBalance]);

  const selectStake = useCallback(async (stakeId) => {
    setSelectedStakeId(stakeId);
    setShowStakeModal(false);
    setLoading(true);
    setJoinError(null);

    try {
      const amount = STAKE_AMOUNTS[stakeId] ?? 20;
      const { rooms } = await api.getRooms();
      const room = rooms.find((r) => parseFloat(r.stake) === amount);
      if (!room) throw new Error(`No room configured for ${amount} BIRR yet.`);

      setActiveRoom(room);

      const { cartelas } = await api.getCartelas(room.id);
      setAvailableCartelas(cartelas);

      // The server's reply ("lobby_state" or "game_state") drives everything
      // else — see the listeners above.
      socketService.selectStake(room.id);
    } catch (err) {
      setJoinError(err.message);
      setShowStakeModal(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const selectCartela = useCallback(
    (cartelaId) => {
      if (!activeRoom) return;
      socketService.selectCartela(activeRoom.id, cartelaId);
    },
    [activeRoom],
  );

  const deselectCartela = useCallback(
    (cartelaId) => {
      if (!activeRoom) return;
      socketService.deselectCartela(activeRoom.id, cartelaId);
    },
    [activeRoom],
  );
  const leaveCartelaSelection = useCallback(() => {
    if (activeRoom) {
      // Tell server to remove this user from the lobby
      socketService.leaveRoom(activeRoom.id);
    }
    setShowCartelaModal(false);
    setActiveRoom(null);
    setLobbyEndsAt(null);
    setTakenCartelaIds([]);
    setMyCartelaIds([]);
    setAvailableCartelas([]);
    setSelectedStakeId(null);
    setJoinError(null);
  }, [activeRoom]);

  const leaveGame = useCallback(() => {
    if (activeRoom) socketService.leaveRoom(activeRoom.id);
    setInGame(false);
    setMyCartelaDetails([]);
    setIsSpectator(false);
    setActiveRoom(null);
    setLobbyEndsAt(null);
    setTakenCartelaIds([]);
    setMyCartelaIds([]);
    setInitialLiveState(null);
    setSelectedStakeId(null);
    refreshBalance();
  }, [activeRoom, refreshBalance]);

  return (
    <GameContext.Provider
      value={{
        showStakeModal,
        showCartelaModal,
        setShowStakeModal,
        setShowCartelaModal,
        inGame,
        isSpectator,
        activeRoom,
        lobbyPlayerCount,
        availableCartelas,
        selectedStakeId,
        lobbyEndsAt,
        takenCartelaIds,
        myCartelaIds,
        initialLiveState,
        loading,
        joinError,
        openStakeModal,
        selectStake,
        selectCartela,
        deselectCartela,
        leaveGame,
        returnToSelection,
        leaveCartelaSelection,
        myCartelaDetails,
        estimatedPot,
        requiredLines,
      }}
    >
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used inside <GameProvider>");
  return ctx;
}
